import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { teamAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, 
  Trophy,
  ChevronLeft,
  Crown,
  Check,
  X,
  Loader2,
  LogOut,
  Camera,
  Trash2
} from 'lucide-react';
import { generateInitials, getEloRank } from '../lib/utils';

export default function TeamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const fetchTeam = async () => {
    try {
      const response = await api.get(`/teams/${id}`);
      setTeam(response.data);
      
      // Fetch applications if captain
      if (response.data.captain_id === user?.id) {
        try {
          const appRes = await api.get(`/teams/${id}/applications`);
          setApplications(appRes.data || []);
        } catch (e) {
          // Not captain or no applications
        }
      }
    } catch (error) {
      toast.error('Team nicht gefunden');
      navigate('/teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, [id]);

  const isCaptain = team?.captain_id === user?.id;
  const isMember = team?.members?.some(m => m.user_id === user?.id);

  const handleAccept = async (userId) => {
    setProcessing(userId);
    try {
      await api.post(`/teams/${id}/accept/${userId}`);
      toast.success('Mitglied akzeptiert!');
      await fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId) => {
    setProcessing(userId);
    try {
      await api.post(`/teams/${id}/reject/${userId}`);
      toast.success('Bewerbung abgelehnt');
      await fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(null);
    }
  };

  const handleLeave = async () => {
    try {
      await api.post(`/teams/${id}/leave`);
      toast.success('Team verlassen');
      navigate('/teams');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleLogoSelect = () => {
    if (isCaptain) {
      logoInputRef.current?.click();
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle ein Bild aus');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Bild zu groß (max 500KB)');
      return;
    }

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        try {
          const response = await teamAPI.uploadLogo(id, { logo_url: base64 });
          setTeam(response.data.team);
          toast.success('Team-Logo aktualisiert!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Fehler beim Verarbeiten des Bildes');
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    try {
      const response = await teamAPI.deleteLogo(id);
      setTeam(response.data.team);
      toast.success('Team-Logo entfernt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-48 rounded-2xl mb-4" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!team) return null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="team-details-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          {/* Team Logo with Upload */}
          <div className="relative group">
            <Avatar className="h-16 w-16 border-2 border-tunis-blue/20">
              <AvatarImage src={team.logo_url} alt={team.name} />
              <AvatarFallback className="bg-gradient-to-br from-tunis-blue to-tunis-teal text-white text-2xl font-bold">
                {generateInitials(team.name)}
              </AvatarFallback>
            </Avatar>
            
            {/* Upload Overlay - only for captain */}
            {isCaptain && (
              <>
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  onClick={handleLogoSelect}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-white" />
                  )}
                </div>
                
                {/* Hidden File Input */}
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleLogoChange}
                  accept="image/*"
                  className="hidden"
                />
                
                {/* Remove Button (if has logo) */}
                {team.logo_url && (
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                    title="Logo entfernen"
                  >
                    <Trash2 className="w-3 h-3 text-white" />
                  </button>
                )}
              </>
            )}
          </div>
          
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-tunis-blue">{team.name}</h1>
            <p className="text-muted-foreground">{team.description || 'Hobby-Team in Tunis'}</p>
            {isCaptain && (
              <p className="text-xs text-tunis-teal mt-1">
                <Camera className="w-3 h-3 inline mr-1" />
                Klicke auf das Logo um es zu ändern
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <Card className="mb-6 border-slate-200" data-testid="team-stats">
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-tunis-blue">{team.member_count}</div>
                <div className="text-sm text-muted-foreground">von {team.max_members || 11} Spieler</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-tunis-teal">{team.team_elo || 1000}</div>
                <div className="text-sm text-muted-foreground">Team-ELO</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-tunis-blue">{team.matches_played || 0}</div>
                <div className="text-sm text-muted-foreground">Spiele</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members */}
        <Card className="mb-6 border-slate-200" data-testid="team-members">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-tunis-teal" />
              Mitglieder ({team.member_count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {team.members?.map((member) => {
                const eloRank = getEloRank(member.elo || 1000);
                return (
                  <div 
                    key={member.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={member.avatar_url} />
                      <AvatarFallback className="bg-tunis-blue/10 text-tunis-blue font-semibold">
                        {generateInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{member.name}</span>
                        {member.user_id === team.captain_id && (
                          <Crown className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`${eloRank.bg} ${eloRank.color} text-xs`}>
                          {eloRank.name}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{member.elo || 1000} ELO</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Applications - only for captain */}
        {isCaptain && applications.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="team-applications">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Bewerbungen ({applications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {applications.map((app) => (
                  <div 
                    key={app.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white border border-amber-200"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={app.avatar_url} />
                      <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold">
                        {generateInitials(app.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <span className="font-medium">{app.name}</span>
                      {app.message && (
                        <p className="text-sm text-muted-foreground">{app.message}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        onClick={() => handleAccept(app.user_id)}
                        disabled={processing === app.user_id}
                      >
                        {processing === app.user_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-50"
                        onClick={() => handleReject(app.user_id)}
                        disabled={processing === app.user_id}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {isMember && !isCaptain && (
          <Button
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            onClick={handleLeave}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Team verlassen
          </Button>
        )}
      </div>
    </Layout>
  );
}
